import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Table, Button, Input, Space, Empty } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import { EyeOutlined, SearchOutlined, SafetyCertificateOutlined, WarningOutlined } from "@ant-design/icons";
import NaoConformidadeModal from "../../../components/NaoConformidadeModal";

function CertificateList() {
  const [certificados, setCertificados] = useState([]);
  const [certificadosFilter, setCertificadosFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [ncModal, setNcModal] = useState({ visible: false, certificado: null });

  function handleSearch(search, key) {
    const searchTermLower = search?.toLowerCase() || "";
    const data = certificados.filter((item) => {
      const value = item[key];
      return value?.toString().toLowerCase().includes(searchTermLower);
    });
    setCertificadosFilter(search ? data : certificados);
  }

  function reset() {
    setCertificadosFilter(certificados);
    setSearchTerm("");
  }

  // Busca geral por SPL ou número do certificado
  function handleGlobalSearch(termo) {
    if (!termo) {
      setCertificadosFilter(certificados);
      return;
    }

    const searchTermLower = termo.toLowerCase();
    const data = certificados.filter((item) => {
      const spl = item.spl?.toString().toLowerCase() || "";
      const numeroCertificado = item.numeroCertificado?.toString().toLowerCase() || "";
      const tag = item.tag?.toString().toLowerCase() || "";
      const numeroSerie = item.numeroSerie?.toString().toLowerCase() || "";

      return (
        spl.includes(searchTermLower) ||
        numeroCertificado.includes(searchTermLower) ||
        tag.includes(searchTermLower) ||
        numeroSerie.includes(searchTermLower)
      );
    });

    setCertificadosFilter(data);
  }

  const columns = [
    {
      title: "Ações",
      dataIndex: "id",
      key: "action",
      width: 100,
      fixed: "left",
      render: (value, record) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
          <Link to={`/cliente/cadastro-certificados/${value}`} className="view-btn">
            <EyeOutlined />
          </Link>
          <button
            className="view-btn nc-flag-btn"
            title="Reportar não conformidade"
            onClick={() => setNcModal({ visible: true, certificado: record })}
          >
            <WarningOutlined />
          </button>
        </div>
      ),
    },
    {
      title: "TAG",
      dataIndex: "tag",
      key: "tag",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Tag"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tag");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Nº Série",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Série"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroSerie");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Cliente",
      dataIndex: "nomeEmpresa",
      key: "nomeEmpresa",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Empresa"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "nomeEmpresa");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidadeCliente",
      key: "unidadeCliente",
    },
    {
      title: "Tipo de Válvula",
      dataIndex: "tipoValvula",
      key: "tipoValvula",
    },
    {
      title: "Tipo de Certificado",
      dataIndex: "tipoCertificado",
      key: "tipoCertificado",
    },
    {
      title: "Nº Certificado",
      dataIndex: "numeroCertificado",
      key: "numeroCertificado",
    },
    {
      title: "SPL",
      dataIndex: "spl",
      key: "spl",
    },
  ];

  const fetchClientsFirebase = async () => {
    const snapshot = await getDocs(collection(db, "clientes"));
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return data;
  };

  const fetchCertificadosFirebase = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "certificados"));
      const certificadosList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const seepilUsuario = JSON.parse(
        sessionStorage.getItem("seepilUsuario")
      );

      const clienteId = seepilUsuario?.cliente_id;

      const unidadesPermitidas =
        Array.isArray(seepilUsuario?.unidadesCliente) &&
        seepilUsuario.unidadesCliente.length > 0
          ? seepilUsuario.unidadesCliente
          : seepilUsuario?.unidadeCliente
          ? [seepilUsuario.unidadeCliente]
          : [];

      if (!clienteId || unidadesPermitidas.length === 0) {
        setCertificados([]);
        setCertificadosFilter([]);
        setLoading(false);
        return;
      }

      const filtered = certificadosList.filter(
        (item) =>
          item.cliente_id === clienteId &&
          unidadesPermitidas.includes(item.unidadeCliente)
      );

      const clients = await fetchClientsFirebase();

      const data = filtered.map((valve) => {
        const client = clients.find((c) => c.id === valve.cliente_id);
        return {
          ...client,
          ...valve,
        };
      });

      setCertificados(data);

      // Verificar se há termo de busca na URL
      const search = searchParams.get("search");
      if (search) {
        setSearchTerm(search);
        const searchTermLower = search.toLowerCase();
        const filteredData = data.filter((item) => {
          const spl = item.spl?.toString().toLowerCase() || "";
          const numeroCertificado = item.numeroCertificado?.toString().toLowerCase() || "";
          const tag = item.tag?.toString().toLowerCase() || "";
          const numeroSerie = item.numeroSerie?.toString().toLowerCase() || "";

          return (
            spl.includes(searchTermLower) ||
            numeroCertificado.includes(searchTermLower) ||
            tag.includes(searchTermLower) ||
            numeroSerie.includes(searchTermLower)
          );
        });
        setCertificadosFilter(filteredData);
      } else {
        setCertificadosFilter(data);
      }

      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar certificados:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificadosFirebase();
  }, [searchParams]);

  return (
    <>
    <Container userType="cliente">
      <div className="certificate-list-container">
        <div className="list-card">
          <div className="list-header">
            <div className="list-header-left">
              <div className="list-icon">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <h2 className="list-title">Certificados Cadastrados</h2>
                <p className="list-subtitle">
                  {searchTerm
                    ? `${certificadosFilter.length} resultado(s) para "${searchTerm}"`
                    : `${certificadosFilter.length} certificados encontrados`}
                </p>
              </div>
            </div>
            {searchTerm && (
              <Button onClick={reset} size="small">
                Limpar busca
              </Button>
            )}
          </div>

          <Table
            dataSource={certificadosFilter}
            columns={columns}
            rowKey="id"
            loading={loading}
            className="custom-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    searchTerm
                      ? `Não localizamos certificados para "${searchTerm}"`
                      : "Nenhum certificado encontrado"
                  }
                />
              ),
            }}
          />
        </div>
      </div>
    </Container>

    <NaoConformidadeModal
      visible={ncModal.visible}
      certificado={ncModal.certificado}
      onClose={() => setNcModal({ visible: false, certificado: null })}
    />
    </>
  );
}

export default CertificateList;
